import { useState, useEffect } from "react";
import { useAuthContext } from "../../context/Context";
import { authAPI, getErrorType, API_ERRORS } from "@/services/api.util";
import { setUserToStorage } from "@/services/auth.util";

export default function OTPVerificationModal({ initialMobileNumber, onVerificationSuccess }) {
  const { loggedInUser, setLoggedInUser } = useAuthContext();
  // Store original mobile number for comparison
  const originalMobileNumber = initialMobileNumber || loggedInUser?.mobileNumber || "";
  
  const [formData, setFormData] = useState({
    phoneNumber: originalMobileNumber,
    otp: ""
  });
  const [mobileNumberChanged, setMobileNumberChanged] = useState(false);

  // Update formData when initialMobileNumber changes
  useEffect(() => {
    if (initialMobileNumber) {
      setFormData(prev => ({
        ...prev,
        phoneNumber: initialMobileNumber
      }));
      setMobileNumberChanged(false);
    }
  }, [initialMobileNumber]);
  
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [otpSent, setOtpSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Validate phone number
  const validatePhoneNumber = () => {
    const newErrors = {};
    
    if (!formData.phoneNumber) {
      newErrors.phoneNumber = "Phone number is required";
    } else if (!/^[0-9+\-\s()]{10,15}$/.test(formData.phoneNumber.replace(/\s/g, ''))) {
      newErrors.phoneNumber = "Please enter a valid phone number (10-15 digits)";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validate OTP
  const validateOTP = () => {
    const newErrors = {};
    
    if (!formData.otp) {
      newErrors.otp = "OTP is required";
    } else if (!/^\d{6}$/.test(formData.otp)) {
      newErrors.otp = "OTP must be exactly 6 digits";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle send phone verification OTP
  const handleSendPhoneVerificationOTP = async (e) => {
    e.preventDefault();
    
    if (!validatePhoneNumber()) {
      return;
    }
    
    setLoading(true);
    setErrors({});

    try {
      await authAPI.sendPhoneVerificationOTP({
        phoneNumber: formData.phoneNumber
      });
      
      setOtpSent(true);
      setResendCooldown(60);
      const cooldownInterval = setInterval(() => {
        setResendCooldown(prev => {
          if (prev <= 1) {
            clearInterval(cooldownInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
    } catch (error) {
      console.error("Send phone verification OTP error:", error);
      
      const errorType = getErrorType(error);
      let errorMessage = "Failed to send OTP. Please try again.";
      
      switch (errorType) {
        case API_ERRORS.VALIDATION_ERROR:
          if (error.data?.errors && Array.isArray(error.data.errors)) {
            const fieldErrors = {};
            error.data.errors.forEach(err => {
              if (err.path) {
                fieldErrors[err.path] = err.msg;
              }
            });
            setErrors(fieldErrors);
            return;
          }
          errorMessage = error.data?.message || "Please check your input and try again.";
          break;
          
        case API_ERRORS.AUTHENTICATION_ERROR:
          errorMessage = error.data?.message || "This phone number is already registered.";
          break;
          
        case API_ERRORS.NETWORK_ERROR:
          errorMessage = "Unable to connect to server. Please check your internet connection.";
          break;
          
        case API_ERRORS.SERVER_ERROR:
          errorMessage = error.data?.message || "Server error. Please try again later.";
          break;
          
        default:
          errorMessage = error.data?.message || error.message || "Failed to send OTP. Please try again.";
      }
      
      setErrors({ general: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  // Handle resend phone verification OTP
  const handleResendPhoneVerificationOTP = async () => {
    if (resendCooldown > 0) return;
    
    setLoading(true);
    setErrors({});

    try {
      await authAPI.sendPhoneVerificationOTP({
        phoneNumber: formData.phoneNumber
      });
      
      setResendCooldown(60);
      const cooldownInterval = setInterval(() => {
        setResendCooldown(prev => {
          if (prev <= 1) {
            clearInterval(cooldownInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      setOtpSent(true);
      
    } catch (error) {
      console.error("Resend phone verification OTP error:", error);
      setErrors({ general: error.data?.message || "Failed to resend OTP. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  // Handle phone verification with OTP
  const handleVerifyPhoneOTP = async (e) => {
    e.preventDefault();
    
    if (!validateOTP()) {
      return;
    }
    
    setLoading(true);
    setErrors({});

    try {
      const response = await authAPI.verifyPhoneOTP({
        phoneNumber: formData.phoneNumber,
        otp: formData.otp
      });
      
      // Note: The backend verifyPhoneOTP endpoint already updates the mobileNumber
      // So we don't need a separate update call here
      
      // Update user data in context and localStorage
      const updatedUser = {
        ...loggedInUser,
        isOTPVerified: true,
        mobileNumber: response.data?.user?.mobileNumber || formData.phoneNumber
      };
      
      setLoggedInUser(updatedUser);
      
      // Update localStorage
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      setUserToStorage({
        ...userData,
        isOTPVerified: true,
        mobileNumber: response.data?.user?.mobileNumber || formData.phoneNumber
      });
      
      // Call success callback
      if (onVerificationSuccess) {
        onVerificationSuccess();
      }
      
    } catch (error) {
      console.error("Phone verification error:", error);
      
      const errorType = getErrorType(error);
      let errorMessage = "Phone verification failed. Please try again.";
      
      switch (errorType) {
        case API_ERRORS.VALIDATION_ERROR:
          if (error.data?.errors && Array.isArray(error.data.errors)) {
            const fieldErrors = {};
            error.data.errors.forEach(err => {
              if (err.path) {
                fieldErrors[err.path] = err.msg;
              }
            });
            setErrors(fieldErrors);
            return;
          }
          errorMessage = error.data?.message || "Please check your input and try again.";
          break;
          
        case API_ERRORS.NETWORK_ERROR:
          errorMessage = "Unable to connect to server. Please check your internet connection.";
          break;
          
        case API_ERRORS.SERVER_ERROR:
          errorMessage = error.data?.message || "Server error. Please try again later.";
          break;
          
        default:
          errorMessage = error.data?.message || error.message || "Phone verification failed. Please try again.";
      }
      
      setErrors({ general: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Track if mobile number has changed
    if (name === 'phoneNumber') {
      const normalizedValue = value.replace(/\s/g, '');
      const normalizedOriginal = originalMobileNumber.replace(/\s/g, '');
      setMobileNumberChanged(normalizedValue !== normalizedOriginal && normalizedValue !== '');
    }
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '20px'
    }}>
      <div style={{
        background: 'linear-gradient(145deg, #ffffff 0%, #f8f9ff 100%)',
        borderRadius: '20px',
        padding: '40px 30px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3), 0 10px 30px rgba(102, 126, 234, 0.2)',
        border: '1px solid rgba(255, 255, 255, 0.8)',
        maxWidth: '500px',
        width: '100%',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative background */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(45deg, rgba(102, 126, 234, 0.03) 0%, transparent 25%, transparent 75%, rgba(118, 75, 162, 0.03) 100%)',
          pointerEvents: 'none',
          zIndex: 0
        }}></div>
        
        <div style={{ position: 'relative', zIndex: 1 }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '30px' }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#000000',
              marginBottom: '8px'
            }}>
              Verify Your Phone Number
            </h2>
            <p style={{ color: '#6c757d', fontSize: '14px' }}>
              {otpSent ? (
                <>
                  We&apos;ve sent a 6-digit OTP to <strong>{formData.phoneNumber}</strong> via WhatsApp
                </>
              ) : (
                <>
                  Please verify your phone number to continue. We&apos;ll send an OTP via WhatsApp.
                </>
              )}
            </p>
            {otpSent && (
              <p style={{ fontSize: '12px', color: '#718096', marginTop: '5px' }}>
                Please check your WhatsApp messages and enter the OTP below
              </p>
            )}
          </div>

          {/* General error message */}
          {errors.general && (
            <div style={{
              padding: "12px 16px",
              backgroundColor: "#f8d7da",
              border: "1px solid #f5c6cb",
              borderRadius: "8px",
              color: "#721c24",
              marginBottom: "20px",
              fontSize: '14px'
            }}>
              {errors.general}
            </div>
          )}

          {/* Form */}
          <form onSubmit={otpSent ? handleVerifyPhoneOTP : handleSendPhoneVerificationOTP} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Phone Number Input (shown if OTP not sent yet) */}
            {!otpSent && (
              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: '14px', 
                  fontWeight: '500', 
                  color: '#2d3748', 
                  marginBottom: '6px' 
                }}>
                  Phone Number <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <input 
                  required 
                  type="tel" 
                  name="phoneNumber"
                  placeholder="Enter your phone number (e.g., 9876543210)"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: errors.phoneNumber ? '2px solid #dc3545' : '2px solid #e2e8f0',
                    borderRadius: '10px',
                    fontSize: '14px',
                    transition: 'all 0.2s ease',
                    backgroundColor: '#fff',
                    outline: 'none'
                  }}
                />
                {errors.phoneNumber && (
                  <div style={{
                    color: "#dc3545",
                    fontSize: "12px",
                    marginTop: "4px"
                  }}>
                    {errors.phoneNumber}
                  </div>
                )}
                <p style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
                  {mobileNumberChanged ? (
                    <span style={{ color: '#667eea', fontWeight: '500' }}>
                      Mobile number changed. OTP will be sent to the new number.
                    </span>
                  ) : (
                    "We'll send an OTP to this number via WhatsApp"
                  )}
                </p>
              </div>
            )}

            {/* OTP Input (shown after OTP is sent) */}
            {otpSent && (
              <>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '14px', 
                    fontWeight: '500', 
                    color: '#2d3748', 
                    marginBottom: '6px' 
                  }}>
                    Enter 6-Digit OTP
                  </label>
                  <input 
                    required 
                    type="text" 
                    name="otp"
                    placeholder="000000"
                    value={formData.otp}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setFormData(prev => ({ ...prev, otp: value }));
                      if (errors.otp) {
                        setErrors(prev => ({ ...prev, otp: '' }));
                      }
                    }}
                    disabled={loading}
                    maxLength={6}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: errors.otp ? '2px solid #dc3545' : '2px solid #e2e8f0',
                      borderRadius: '10px',
                      fontSize: '24px',
                      textAlign: 'center',
                      letterSpacing: '8px',
                      fontFamily: 'monospace',
                      fontWeight: '600',
                      transition: 'all 0.2s ease',
                      backgroundColor: '#fff',
                      outline: 'none'
                    }}
                  />
                  {errors.otp && (
                    <div style={{
                      color: "#dc3545",
                      fontSize: "12px",
                      marginTop: "4px"
                    }}>
                      {errors.otp}
                    </div>
                  )}
                </div>

                {/* Resend OTP */}
                <div style={{ textAlign: 'center' }}>
                  <p style={{ fontSize: '14px', color: '#6c757d', marginBottom: '10px' }}>
                    Didn&apos;t receive the OTP?
                  </p>
                  <button
                    type="button"
                    onClick={handleResendPhoneVerificationOTP}
                    disabled={loading || resendCooldown > 0}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: resendCooldown > 0 ? '#9ca3af' : '#667eea',
                      cursor: (loading || resendCooldown > 0) ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      textDecoration: 'underline',
                      padding: '5px 10px'
                    }}
                  >
                    {resendCooldown > 0 ? `Resend OTP in ${resendCooldown}s` : 'Resend OTP'}
                  </button>
                </div>
              </>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '14px 20px',
                backgroundColor: "#000",
                color: "#fff",
                border: "none",
                borderRadius: '10px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.7 : 1,
                transition: 'all 0.2s ease',
                marginTop: '10px'
              }}
            >
              {loading ? (
                <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                  <span>{otpSent ? "Verifying..." : "Sending OTP..."}</span>
                  <div style={{
                    width: "16px",
                    height: "16px",
                    border: "2px solid #fff",
                    borderTop: "2px solid transparent",
                    borderRadius: "50%",
                    animation: "otpSpinner 1s linear infinite"
                  }}></div>
                </span>
              ) : (
                otpSent ? "Verify Phone Number" : "Send OTP via WhatsApp"
              )}
            </button>
          </form>
        </div>
      </div>
      
      <style>
        {`
          @keyframes otpSpinner {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
}
